import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../context'
import { query } from '../graphql'
import { trackEvent } from '../helpers/analytics'
import { localStorage } from '../helpers/local-storage'
import {
  getRedirectAfterRemoval,
  getSampleDataAvailability,
  getSampleDataGate,
  isProtectedSamplePartner,
  isSampleCareer,
  isSampleSpace,
  normalizeSampleDataScope,
  normalizeSampleDataSummary,
  parseControlResult,
  runRemoveLoop,
  sampleCareerIdWithMust,
  sampleCareerIdWithoutMust
} from '../helpers/sample-data'
import {
  dismissSampleDataHint,
  getDismissedSampleDataHintPartnerId,
  resetSampleDataHintDismissal,
  subscribeSampleDataHint
} from '../helpers/sample-data-hint-dismissal'
import { useCareersQuery } from './use-careers-query'

const EMPTY = []

const selectSampleCareers = (careers) => careers.filter(isSampleCareer)

// Nach Entfernen und Hinzufügen, und als Self-Heal, wenn der Dialog nichts
// mehr findet. Wirft nicht, invalidate/reset schlucken Refetch-Fehler.
const refreshSampleDataQueries = async (
  queryClient,
  partnerId,
  {
    staleCareerIds = [],
    staleSpaceIds = [],
    keptCareerIds = [],
    keptSpaceIds = [],
    resetTalents = true
  } = {}
) => {
  staleCareerIds.forEach((id) =>
    queryClient.removeQueries({ queryKey: QUERY_KEYS.careerById(id) })
  )
  staleSpaceIds.forEach((id) =>
    queryClient.removeQueries({ queryKey: QUERY_KEYS.spaceById(id) })
  )

  // Hygiene aus der alten Auto-Erzeugung in Discover
  localStorage.removeItem('demo-talents')

  // Die ganze Familie QUERY_KEYS.talentsByPartner für alle Usernames. Der
  // Refetch lädt voll, solange Beispiel-Spaces im Cache sind (C1). Der
  // Volllauf kann bei großen Partnern dauern, Dialog und Toast warten nicht
  // darauf, deshalb ohne await (Refetch-Fehler schluckt react-query).
  const talents = { queryKey: ['talents', partnerId] }
  if (resetTalents) {
    queryClient.resetQueries(talents)
  } else {
    queryClient.invalidateQueries(talents)
  }

  await Promise.all(
    [
      QUERY_KEYS.careersByPartner(partnerId),
      QUERY_KEYS.careersWithAnalysesByPartner(partnerId),
      QUERY_KEYS.spacesByPartner(partnerId),
      QUERY_KEYS.recoverTalentsByPartner(partnerId),
      QUERY_KEYS.teamAnalysesByPartner(partnerId),
      QUERY_KEYS.sampleDataSummary(partnerId),
      // Zur normalen Stelle gewordene Beispielstellen verlieren das Label,
      // auch auf ihrer gerade offenen Detailseite
      ...keptCareerIds.map((id) => QUERY_KEYS.careerById(id)),
      ...keptSpaceIds.map((id) => QUERY_KEYS.spaceById(id))
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  )
}

const useSampleDataGateArgs = () => {
  const { partner } = usePartnerContext()
  const { activeSpace } = useUserContext()

  return {
    partnerId: partner.id,
    canDelete: activeSpace?.authorization_role?.crud?.delete === true,
    // wie sidebar.js: Discover-Partner sehen vor dem Onboarding nur Discover
    isDiscoverVisible: partner.isDashboardComponentVisible?.discover === true,
    hasCompletedOnboarding: activeSpace?.hasCompletedOnboarding === true
  }
}

/**
 * Beispieldaten in den geladenen Listen. Stellen kommen roh aus
 * useCareersQuery (inklusive archivierter), Talente übergibt die Seite
 * (data.spaces von /talents oder /recover/talents).
 */
const useSampleDataPresence = ({ spaces } = {}) => {
  const { data: careers, isPending } = useCareersQuery({
    select: selectSampleCareers
  })

  const sampleSpaces = useMemo(
    () => (spaces || EMPTY).filter(isSampleSpace),
    [spaces]
  )
  const sampleCareers = careers || EMPTY

  return {
    careers: sampleCareers,
    spaces: sampleSpaces,
    hasSampleData: sampleCareers.length > 0 || sampleSpaces.length > 0,
    isPending
  }
}

/**
 * Was der Nutzer mit Beispieldaten tun darf, ohne Abfrage. `gate` ist null,
 * wenn Entfernen erlaubt ist, sonst TEMPLATE_PARTNER, NO_PERMISSION oder
 * ONBOARDING (Werte aus SAMPLE_DATA_AVAILABILITY). Hinzufügen verlangt in der
 * UI wie Entfernen das Löschrecht (das Backend erlaubt schon __create, damit
 * eingeladene WORKER das Onboarding durchlaufen).
 */
export const useSampleDataAccess = () => {
  const args = useSampleDataGateArgs()
  const gate = getSampleDataGate(args)
  const isTemplatePartner = isProtectedSamplePartner(args.partnerId)

  return {
    ...args,
    gate,
    canRemove: gate === null,
    canAdd: args.canDelete && !isTemplatePartner,
    isTemplatePartner
  }
}

/**
 * availability: NONE | PRESENT | NO_PERMISSION | ONBOARDING | TEMPLATE_PARTNER
 * (SAMPLE_DATA_AVAILABILITY). Für Hinweis und Menüeinträge.
 */
export const useSampleDataAvailability = ({ spaces } = {}) => {
  const presence = useSampleDataPresence({ spaces })
  const access = useSampleDataAccess()

  const availability = getSampleDataAvailability({
    partnerId: access.partnerId,
    canDelete: access.canDelete,
    isDiscoverVisible: access.isDiscoverVisible,
    hasCompletedOnboarding: access.hasCompletedOnboarding,
    hasSampleData: presence.hasSampleData
  })

  return { ...access, availability, presence }
}

/**
 * Hinweis auf Beispieldaten per X ausgeblendet, bis zum nächsten Reload und
 * für alle Listen des aktuellen Partners.
 */
export const useSampleDataHintDismissal = () => {
  const { partner } = usePartnerContext()
  const dismissedPartnerId = useSyncExternalStore(
    subscribeSampleDataHint,
    getDismissedSampleDataHintPartnerId
  )

  const dismiss = useCallback(
    () => dismissSampleDataHint(partner?.id),
    [partner?.id]
  )

  return {
    isDismissed: !!partner?.id && dismissedPartnerId === partner.id,
    dismiss
  }
}

/**
 * Frische Zählung beim Backend (getSampleDataSummary). Beim Öffnen von
 * Dialog oder Settings wird immer neu geladen, bis dahin zeigt der Aufrufer
 * Skeletons (`isFetchedAfterMount`).
 */
export const useSampleDataSummary = ({ scope, enabled = true } = {}) => {
  const { partner } = usePartnerContext()
  const normalizedScope = normalizeSampleDataScope(scope)

  return useQuery({
    queryKey: QUERY_KEYS.sampleDataSummary(partner.id, normalizedScope),
    queryFn: async () => {
      const raw = await query({
        query: 'sampleDataControl',
        variables: {
          action: 'getSampleDataSummary',
          partner_id: partner.id,
          ...normalizedScope
        }
      })

      return normalizeSampleDataSummary(
        parseControlResult(raw, 'getSampleDataSummary')
      )
    },
    enabled: enabled && !!partner.id,
    refetchOnMount: 'always',
    retry: 1
  })
}

/**
 * Entfernt Beispieldaten hart über removeSampleData, global oder im Scope.
 * mutateAsync({ scope?, source }) liefert das summierte Ergebnis oder wirft
 * (blocked, INCOMPLETE nach 10 Runden, Netzwerk). Die Caches werden in jedem
 * Fall aufgefrischt, weil auch ein abgebrochener Lauf schon gelöscht haben kann.
 * Liegt die Seite auf einer entfernten Stelle oder einem entfernten Talent,
 * geht es zurück auf die Liste. resetTalents: false für Aufrufer, die unter
 * dem Ladezustand von /talents hängen (DeleteSpace). Ohne Reset bleibt die
 * Liste stehen, der Volllauf kommt über die C1-Regel.
 */
export const useRemoveSampleData = ({
  redirect = true,
  resetTalents = true
} = {}) => {
  const { partner } = usePartnerContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return useMutation({
    mutationFn: async ({ scope, source } = {}) => {
      const partnerId = partner.id
      const scopeVariables = normalizeSampleDataScope(scope)
      const trackingParams = { source, scoped: !!scopeVariables }

      let result
      let partial

      try {
        result = await runRemoveLoop(async ({ cursor }) => {
          const raw = await query({
            query: 'sampleDataControl',
            variables: {
              action: 'removeSampleData',
              // explizit, damit Admin-Impersonation den angezeigten Partner trifft
              partner_id: partnerId,
              ...scopeVariables,
              cursor
            }
          })

          return parseControlResult(raw, 'removeSampleData')
        })

        trackEvent(trackEvents.SAMPLE_DATA_REMOVE_SUCCEEDED, {
          ...trackingParams,
          careers: result.removed.careers,
          talents: result.removed.talents,
          kept: result.kept_careers.length,
          rounds: result.rounds
        })

        return result
      } catch (err) {
        partial = err?.result

        trackEvent(trackEvents.SAMPLE_DATA_REMOVE_FAILED, {
          ...trackingParams,
          reason: err?.code || err?.message,
          rounds: partial?.rounds
        })

        throw err
      } finally {
        const done = result || partial

        // Erst weg von der Detailseite, dann deren Query entfernen. Auch nach
        // einem Abbruch, falls die Stelle oder das Talent schon gelöscht ist.
        const target = redirect ? getRedirectAfterRemoval(pathname, done) : null
        if (target) navigate(target, { replace: true })

        // Gefilterte Talentliste könnte auf eine gelöschte Stelle zeigen
        // (Muster assessment-settings.js)
        if (done?.removed_career_ids?.length) {
          localStorage.setItem('/talents', '')
        }

        await refreshSampleDataQueries(queryClient, partnerId, {
          staleCareerIds: done?.removed_career_ids,
          staleSpaceIds: done?.removed_space_ids,
          keptCareerIds: (done?.kept_careers || []).map(({ id }) => id),
          // Nach Erfolg voll neu laden, nach Fehler ohne Ladezustand
          // auffrischen. Der Volllauf kommt dann über die C1-Regel.
          resetTalents: resetTalents && !!result
        })
      }
    }
  })
}

// generateDemoData überspringt behaltene Stellen samt ihrer Talente. Wurde
// dabei gar nichts angelegt, ist das kein Erfolg. Ältere Backends liefern
// keine Zähler (res null), das zählt weiter als Erfolg.
const isNothingAdded = (res) => {
  if (!res?.created) return false

  const created =
    (Number(res.created.careers) || 0) + (Number(res.created.talents) || 0)
  const skipped =
    (Number(res.skipped?.careers) || 0) + (Number(res.skipped?.talents) || 0)

  return created === 0 && skipped > 0
}

/**
 * Legt Beispieldaten über helperControl generateDemoData an (idempotent, eine
 * behaltene Stelle wird nicht überschrieben). Die Bestätigung holt der Dialog
 * ein. notify: Erfolgsmeldung zeigen (Discover-Onboarding: false). Wurde
 * nichts angelegt, kommt statt der Erfolgsmeldung ein Hinweis.
 */
export const useAddSampleData = ({ notify = true, source } = {}) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()
  const { success, error } = useNotificationContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ source: callSource } = {}) => {
      const partnerId = partner.id

      const raw = await query({
        query: 'helperControl',
        variables: {
          action: 'generateDemoData',
          partner_id: partnerId,
          language
        }
      })

      // { created: { careers, talents }, skipped: {…} }. Ältere Backends
      // liefern keine Zähler, das ist kein Fehler.
      let res = null
      try {
        res = parseControlResult(raw, 'generateDemoData')
      } catch {
        res = null
      }

      if (!isNothingAdded(res)) {
        resetSampleDataHintDismissal()
        trackEvent(trackEvents.SAMPLE_DATA_ADD_SUCCEEDED, {
          source: callSource || source,
          careers: res?.created?.careers,
          talents: res?.created?.talents
        })
      }

      // generateDemoData schreibt per put auf feste IDs, auch über
      // archivierte Beispielstellen
      await refreshSampleDataQueries(queryClient, partnerId, {
        staleCareerIds: [
          sampleCareerIdWithMust(partnerId),
          sampleCareerIdWithoutMust(partnerId)
        ]
      })

      return res || {}
    },
    onSuccess: (res) => {
      if (!notify) return

      if (isNothingAdded(res)) {
        error(t('sample_data.add.nothing_added'))
        return
      }

      success(t('sample_data.add.success'))
    },
    onError: (err) => {
      // NOT_AUTHORIZED meldet der query()-Wrapper bereits selbst
      if (err?.message !== 'NOT_AUTHORIZED') error(t('sample_data.add.error'))
    }
  })
}

// Self-Heal, wenn der Dialog keine Beispieldaten mehr findet. scope ist der
// Dialog-Scope ({ career_ids?, space_ids? }).
export const useRefreshSampleData = () => {
  const { partner } = usePartnerContext()
  const queryClient = useQueryClient()

  return useCallback(
    (scope) =>
      refreshSampleDataQueries(queryClient, partner.id, {
        // Offene Detailseite einer inzwischen behaltenen oder gelöschten
        // Beispielstelle neu laden, sonst bleiben Badge und Weiche stehen
        keptCareerIds: scope?.career_ids || [],
        keptSpaceIds: scope?.space_ids || []
      }),
    [queryClient, partner.id]
  )
}
